import { useEffect, useState } from 'react';
import {
  AutocompleteInput,
  DateTimeInput,
  SaveButton,
  SimpleForm,
  Title,
  Toolbar,
} from 'react-admin';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { fetchBlob } from 'shared/utils/blob-fetch';
import styled from 'styled-components';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Button,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

type CompanyItem = {
  id: string;
  name: string;
};

type B2bStatsFilter = {
  startDate?: string;
  endDate?: string;
  companyId: string;
};

type B2bStatsFormValues = {
  company?: string;
  startDate?: Date | string;
  endDate?: Date | string;
};

type DownloadState = {
  data?: Blob;
  filename: string;
  filesize: number;
  error?: string;
  errors: string[];
};

const emptyDownloadState = (defaultFilename: string): DownloadState => ({
  filename: defaultFilename,
  filesize: 0,
  errors: [],
});

const B2BStatsXlsDonwload = () => {
  const [filter, setFilter] = useState<B2bStatsFilter>({
    companyId: '',
    endDate: '',
    startDate: '',
  });

  const [aggregateStats, setAggregateStats] = useState<DownloadState>(() =>
    emptyDownloadState('b2b-stats.xlsx'),
  );
  const [sessionsStats, setSessionsStats] = useState<DownloadState>(() =>
    emptyDownloadState('b2b-sessions-stats.xlsx'),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [formError, setFormError] = useState<string | undefined>();

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL_ADMIN}${API_URL.companies}?limit=100&page=1`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res) => res.json())
      .then((response: { data: CompanyItem[] }) => {
        setCompanies(response.data);
      });
  }, []);

  useEffect(() => {
    if (!filter.companyId) return;

    const params = buildFilterParams(filter);

    setIsLoading(true);
    setAggregateStats((prev) => ({
      ...emptyDownloadState('b2b-stats.xlsx'),
      filename: prev.filename,
    }));
    setSessionsStats((prev) => ({
      ...emptyDownloadState('b2b-sessions-stats.xlsx'),
      filename: prev.filename,
    }));

    Promise.all([
      fetchB2bStatsBlob(
        `${API_BASE_URL_ADMIN}${API_URL.statsB2B}?${params.toString()}`,
        'b2b-stats.xlsx',
      ),
      fetchB2bStatsBlob(
        `${API_BASE_URL_ADMIN}${API_URL.statsB2BSessions}?${params.toString()}`,
        'b2b-sessions-stats.xlsx',
      ),
    ])
      .then(([aggregateResult, sessionsResult]) => {
        setAggregateStats(aggregateResult);
        setSessionsStats(sessionsResult);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const download = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);

    const tempAnchor = document.createElement('a');
    tempAnchor.setAttribute('href', url);
    tempAnchor.setAttribute('download', filename);
    tempAnchor.click();
    tempAnchor.remove();
  };

  const handleCompanyChange = () => {
    setFormError(undefined);
    setAggregateStats((prev) => ({ ...prev, error: undefined, errors: [] }));
    setSessionsStats((prev) => ({ ...prev, error: undefined, errors: [] }));
  };

  const handleSubmit = ({ startDate, endDate, company }: B2bStatsFormValues) => {
    setFormError(undefined);
    setAggregateStats((prev) => ({ ...prev, error: undefined, errors: [] }));
    setSessionsStats((prev) => ({ ...prev, error: undefined, errors: [] }));

    if (!company) {
      setFormError('Выберите компанию');
      return;
    }

    const nextFilter: B2bStatsFilter = {
      companyId: company,
    };

    if (startDate && typeof startDate !== 'string') {
      nextFilter.startDate = startDate?.toISOString();
    }

    if (endDate && typeof endDate !== 'string') {
      nextFilter.endDate = endDate?.toISOString();
    }

    setFilter(nextFilter);
  };

  const hasFetchError =
    aggregateStats.error !== undefined || sessionsStats.error !== undefined;

  return (
    <Root>
      <Title title="Выгрузка B2B" />

      <Card sx={{ mb: 2 }}>
        <CardHeader title="Параметры выгрузки" />

        {formError && (
          <Alert severity="error" variant="filled" sx={{ m: 2 }}>
            {formError}
          </Alert>
        )}

        {(aggregateStats.error || sessionsStats.error) && (
          <Alert severity="error" variant="filled" sx={{ m: 2 }}>
            {aggregateStats.error && (
              <>
                {aggregateStats.error}
                {aggregateStats.errors.length > 0 && ':'}
                {aggregateStats.errors.length > 0 && (
                  <List>
                    {aggregateStats.errors.map((item) => (
                      <ListItem key={`aggregate-${item}`}>{item}</ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
            {aggregateStats.error && sessionsStats.error && <Divider sx={{ my: 1 }} />}
            {sessionsStats.error && (
              <>
                {sessionsStats.error}
                {sessionsStats.errors.length > 0 && ':'}
                {sessionsStats.errors.length > 0 && (
                  <List>
                    {sessionsStats.errors.map((item) => (
                      <ListItem key={`sessions-${item}`}>{item}</ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Alert>
        )}

        <SimpleForm
          onSubmit={handleSubmit}
          toolbar={
            <Toolbar>
              <SaveButton label="Изменить" disabled={isLoading} />
            </Toolbar>
          }
        >
          <Grid>
            <DateTimeInput
              label="Начало периода"
              source="startDate"
              sx={{ mr: 2 }}
            />
            <DateTimeInput label="Конец периода" source="endDate" />
          </Grid>
          <div>
            <AutocompleteInput
              label="Компания"
              source="company"
              choices={companies}
              optionText="name"
              optionValue="id"
              sx={{ minWidth: 600 }}
              onChange={handleCompanyChange}
            />
          </div>
        </SimpleForm>
      </Card>

      {isLoading ? (
        <LinearProgress color="primary" sx={{ mb: 2 }} />
      ) : (
        <LinearProgress
          variant="determinate"
          value={0}
          sx={{ visibility: 'hidden', mb: 2 }}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardHeader title="Выгрузка статистики по B2B-клиентам" />
        <CardContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{ my: 1 }}
              disabled={
                isLoading || formError !== undefined || hasFetchError || !aggregateStats.data
              }
              onClick={() =>
                aggregateStats.data &&
                download(aggregateStats.data, aggregateStats.filename)
              }
            >
              Скачать
            </Button>
            <Typography sx={{ mx: 2 }}>
              {formatBytes(aggregateStats.filesize)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Скачать статистику по сессиям B2B-клиентов" />
        <CardContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{ my: 1 }}
              disabled={
                isLoading || formError !== undefined || hasFetchError || !sessionsStats.data
              }
              onClick={() =>
                sessionsStats.data &&
                download(sessionsStats.data, sessionsStats.filename)
              }
            >
              Скачать
            </Button>
            <Typography sx={{ mx: 2 }}>
              {formatBytes(sessionsStats.filesize)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Root>
  );
};

export default B2BStatsXlsDonwload;

const Root = styled.div`
  padding: 20px;
`;

function buildFilterParams(filter: B2bStatsFilter) {
  const params = new URLSearchParams();
  const { startDate, endDate, companyId } = filter;

  if (startDate) {
    params.set('startDate', startDate);
  }

  if (endDate) {
    params.set('endDate', endDate);
  }

  if (companyId) {
    params.set('companyId', companyId);
  }

  return params;
}

async function fetchB2bStatsBlob(
  url: string,
  defaultFilename: string,
): Promise<DownloadState> {
  const state = emptyDownloadState(defaultFilename);
  const result = await fetchBlob(url, {
    defaultFilename,
    fallbackErrorMessage: 'Не удалось загрузить файл',
  });

  return {
    ...state,
    data: result.blob,
    filename: result.filename,
    filesize: result.filesize,
    error: result.error,
    errors: result.errors ?? [],
  };
}

function formatBytes(bytes: number, decimals: number = 2) {
  if (bytes === 0) {
    return '0 B';
  }

  var k = 1024,
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
  );
}
